<script setup lang="ts">
import SectionTitle from '@/components/atoms/SectionTitle.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import {
  type UserCourseViewInstructorFragment,
  type UserCourseViewKollegeUserFragment,
  useUpdateKollegeUserCourseInstructorMutation,
} from './UserCourseView.generated'
import { computed, ref } from 'vue'
import { useAlert } from '@/libs/alertPortal'

const props = defineProps<{
  user: UserCourseViewKollegeUserFragment
  allInstructors: UserCourseViewInstructorFragment[]
}>()

const alertPortal = useAlert()

const { mutate, loading } = useUpdateKollegeUserCourseInstructorMutation()

const userCourse = computed(() => {
  return props.user.standardCourse
})

const selectorOptions = computed(() => {
  return [
    {
      text: '未選択',
      value: 'unselected',
    },
    ...props.allInstructors.map(instructor => {
      return {
        text: instructor.name,
        value: instructor.id,
      }
    }),
  ]
})

const handleSave = async () => {
  if (!props.user.standardCourse) {
    alertPortal.error('コースが選択されていません')
    return
  }
  const result = await mutate({
    userCourseID: props.user.standardCourse.id,
    instructorID: instructorId.value,
  })
  if (result?.errors) {
    alertPortal.error(result?.errors[0]?.message)
    return
  }

  instructorId.value =
    result?.data?.updateKollegeUserCourseInstructor?.instructor?.id ??
    'unselected'

  alertPortal.success('保存しました')
}

// InstructorId
// nullはちゃんと表示できないので、nullの場合だけ'unselected'にする。未選択の場合は保存ボタンをDisabledにする
const instructorId = ref(userCourse.value?.instructor?.id ?? 'unselected')
</script>
<template>
  <section :class="$style.main">
    <SectionTitle :class="$style.title">コース</SectionTitle>
    <dl :class="$style.infos">
      <div :class="$style.info">
        <dt>担当講師</dt>
        <dd>
          <DolphinSelector v-model="instructorId" :options="selectorOptions" />
        </dd>
      </div>
    </dl>
    <FlatButton
      :class="$style.saveButton"
      :disabled="loading || instructorId === 'unselected'"
      @click="handleSave"
    >
      保存
    </FlatButton>
  </section>
</template>

<style lang="scss" module>
.title {
  padding-bottom: 24px;
  border-bottom: 1px solid $color-border-low-emphasis;
}

.main {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.infos {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.info {
  display: flex;
  flex-direction: column;
  gap: 4px;

  dt {
    font-weight: bold;
  }
}

.saveButton {
  align-self: flex-end;
}
</style>
